/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from "react";
import DataTable, { Column } from "components/DataTable";
import GenericModal from "components/Modal";
import GenericToaster from "components/Toaster";
import DeletePopup from "components/DeletePopup/deletePopup";
import { FaEdit, FaRegTrashAlt } from "react-icons/fa";
import { CalendarRange } from "lucide-react";
import leavePolicyManager from "services/LeavePolicy/leavePolicyManager";
import companyLeaveSettingsManager from "services/LeavePolicy/companyLeaveSettingsManager";
// NOTE: shared resolver so a string `response.data` and an object
// `response.data` both end up as a readable toast message.
import { getApiErrorMessage } from "util/errorMessage";
import { LeavePolicy as LeavePolicyRecord, getMonthlyAccrual } from "services/LeavePolicy/leavePolicyInterface";
import LeavePolicyForm from "./LeavePolicyForm";
import YearStartMonthDialog from "./YearStartMonthDialog";

// 1-based month → full label, for the "Leave year start" button.
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/* ────────────────────────────────────────────────────────────────────────
   Leave Policy — admin screen to define annual leave entitlements per type.

   Lists the configured policies and opens a modal to add / edit one. Accrual
   policies show their per-month figure inline. Follows the app's standard
   listing pattern (DataTable + GenericModal + GenericToaster).
   ──────────────────────────────────────────────────────────────────────── */

// Yes/No pill (theme-aware) for the accrual column.
const AccrualBadge: React.FC<{ value: boolean }> = ({ value }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${value
      ? "text-emerald-400 dark:bg-emerald-900/30 dark:text-emerald-400"
      : "text-slate-600 dark:bg-slate-700 dark:text-slate-300"
      }`}
  >
    {value ? "Yes" : "No"}
  </span>
);

const LeavePolicy: React.FC = () => {
  const [policies, setPolicies] = useState<LeavePolicyRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const [addModal, setAddModal] = useState(false);
  const [updateModal, setUpdateModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<LeavePolicyRecord | null>(null);

  // Company leave-year start month (1–12), set via the Year Start dialog.
  const [yearStartMonth, setYearStartMonth] = useState<number | null>(null);
  const [monthModal, setMonthModal] = useState(false);

  const [showSuccessToaster, setShowSuccessToaster] = useState(false);
  const [showErrorToaster, setShowErrorToaster] = useState(false);
  const [message, setMessage] = useState("");

  const notifySuccess = (msg: string) => {
    setMessage(msg);
    setShowSuccessToaster(true);
  };
  const notifyError = (msg: string) => {
    setMessage(msg);
    setShowErrorToaster(true);
  };
  const closeToaster = () => {
    setShowSuccessToaster(false);
    setShowErrorToaster(false);
  };

  const fetchPolicies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await leavePolicyManager.getAll();
      const payload = res.data as any;
      const data: LeavePolicyRecord[] = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.content)
          ? payload.content
          : Array.isArray(payload?.data)
            ? payload.data
            : [];
      setPolicies(data);
    } catch (error) {
      console.error("Failed to fetch leave policies:", error);
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Company leave-year start month (reflected on the Year Start button).
  const fetchYearStart = useCallback(async () => {
    try {
      const res = await companyLeaveSettingsManager.get();
      setYearStartMonth(Number(res.data?.yearStartMonth) || null);
    } catch (error) {
      console.error("Failed to fetch company leave settings:", error);
      setYearStartMonth(null);
    }
  }, []);

  useEffect(() => {
    void fetchPolicies();
    void fetchYearStart();
  }, [fetchPolicies, fetchYearStart]);

  const handleAdd = () => {
    setSelectedRow(null);
    setAddModal(true);
  };
  const handleEdit = (row: LeavePolicyRecord) => {
    setSelectedRow(row);
    setUpdateModal(true);
  };
  const handleDelete = (row: LeavePolicyRecord) => {
    setSelectedRow(row);
    setDeleteModal(true);
  };

  const onSaved = (msg: string) => {
    setAddModal(false);
    setUpdateModal(false);
    setSelectedRow(null);
    notifySuccess(msg);
    void fetchPolicies();
  };

  const deleteRow = async (id: number) => {
    setDeleteModal(false);
    try {
      // Soft-delete: the list API returns only active policies, so deleting
      // deactivates the policy (isActive=false).
      await leavePolicyManager.remove(id, false);
      notifySuccess("Leave policy deleted successfully");
      await fetchPolicies();
    } catch (error: any) {
      // CHANGE: only `data.message` was read, so a plain-string error body fell
      // through to the generic text and hid the real reason (e.g. "Policy is in
      // use"). The shared resolver handles both shapes.
      notifyError(getApiErrorMessage(error, "Failed to delete leave policy"));
    }
  };

  const columns: Column[] = [
    { key: "leaveType", label: "Type", bold: true },
    {
      key: "daysPerYear",
      label: "Days ",
      bold: true,
      render: (value: any) => <span className="tabular-nums">{value ?? "—"}</span>,
    },
    {
      key: "isAccrual",
      label: "Accrual",
      render: (value: any) => <AccrualBadge value={Boolean(value)} />,
    },
    {
      key: "perMonth",
      label: "Per Month",
      excludeExport: true,
      render: (_value: any, row: any) =>
        row?.isAccrual ? (
          <span className="tabular-nums text-slate-700 dark:text-slate-200">
            {getMonthlyAccrual(row.daysPerYear).perMonthLabel}
          </span>
        ) : (
          <span className="text-slate-400 dark:text-slate-500">—</span>
        ),
    },
    {
      key: "maxCarryForward",
      label: "Max Carry Forward",
      render: (value: any) => <span className="tabular-nums">{value ?? 0}</span>,
    },
    {
      key: "actions",
      label: "Actions",
      excludeExport: true,
      actions: (row: any) => (
        <div className="flex flex-start">
          <span
            onClick={() => handleEdit(row as LeavePolicyRecord)}
            className="btn-primary px-2 py-2 mr-2 rounded-lg cursor-pointer"
            title="Edit policy"
          >
            <FaEdit />
          </span>
          <span
            onClick={() => handleDelete(row as LeavePolicyRecord)}
            className="btn-red px-2 py-2 rounded-lg cursor-pointer"
            title="Delete policy"
          >
            <FaRegTrashAlt />
          </span>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-1 min-h-0 flex-col text-slate-900 dark:text-white bg-white dark:bg-gray-800 rounded-xl shadow-sm transition-colors duration-300">
      {/* Fills the AdminConfig content pane; the table body scrolls internally. */}
      <DataTable
        heightClass="flex-1 min-h-0"
        data={policies as any}
        columns={columns}
        loading={loading}
        onAddBtnClick={handleAdd}
        isExportable={false}
        preAddButtons={
          <button
            type="button"
            onClick={() => setMonthModal(true)}
            title="Set the month the leave year starts"
            className="mr-1 inline-flex h-[38px] items-center justify-center rounded-lg border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 shadow-sm transition-all hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <CalendarRange size={18} className="mr-2" />
            <span>
              From
              {yearStartMonth ? `: ${MONTH_NAMES[yearStartMonth - 1]}` : ""}
            </span>
          </button>
        }
      />

      {addModal && (
        <GenericModal
          isOpen={addModal}
          onClose={() => setAddModal(false)}
          header="Add Leave Policy"
          customWidth="640px"
        >
          <LeavePolicyForm
            onSuccess={onSaved}
            onError={notifyError}
            onClose={() => setAddModal(false)}
          />
        </GenericModal>
      )}

      {updateModal && selectedRow && (
        <GenericModal
          isOpen={updateModal}
          onClose={() => setUpdateModal(false)}
          header="Update Leave Policy"
          customWidth="640px"
        >
          <LeavePolicyForm
            initialValue={selectedRow}
            onSuccess={onSaved}
            onError={notifyError}
            onClose={() => setUpdateModal(false)}
          />
        </GenericModal>
      )}

      {monthModal && (
        <GenericModal
          isOpen={monthModal}
          onClose={() => setMonthModal(false)}
          header="Leave Year Start Month"
          customWidth="520px"
        >
          <YearStartMonthDialog
            initialMonth={yearStartMonth}
            onClose={() => setMonthModal(false)}
            onError={notifyError}
            onSaved={(month, msg) => {
              setYearStartMonth(month);
              setMonthModal(false);
              notifySuccess(msg);
            }}
          />
        </GenericModal>
      )}

      {deleteModal && selectedRow && (
        <GenericModal
          isOpen={deleteModal}
          onClose={() => setDeleteModal(false)}
          header="Delete Leave Policy"
        >
          <DeletePopup
            handleDelete={deleteRow}
            onClose={() => setDeleteModal(false)}
            selectedRow={selectedRow}
            message={`Are you sure you want to delete the "${selectedRow.leaveType}" policy?`}
          />
        </GenericModal>
      )}

      {(showSuccessToaster || showErrorToaster) && (
        <GenericToaster
          message={message}
          onClose={closeToaster}
          open={showSuccessToaster || showErrorToaster}
          severity={showErrorToaster ? "error" : "info"}
        />
      )}
    </div>
  );
};

export default LeavePolicy;
